import type { CarbonIconType } from "@carbon/icons-react";
import {
  CheckmarkFilled,
  InformationFilled,
  Misuse,
  WarningFilled,
  WarningHexFilled,
} from "@carbon/icons-react";
import type { AlertStatus } from "./Alert";

export const ALERT_ICONS: Record<AlertStatus, CarbonIconType> = {
  primary: InformationFilled,
  default: InformationFilled,
  info: InformationFilled,
  success: CheckmarkFilled,
  warning: WarningFilled,
  danger: WarningHexFilled,
  critical: Misuse,
};

export type AlertStatusClassNames = {
  base?: string;
  indicator?: string;
  content?: string;
  title?: string;
  description?: string;
};

/**
 * Colors with no matching HeroUI alert status need an explicit foreground override:
 * `critical` maps to `danger`, `info` maps to `default`. Other colors rely on HeroUI
 * alert status styles (`text-*-soft-foreground`).
 */
const CRITICAL_FOREGROUND = "!text-critical-soft-foreground";
const INFO_FOREGROUND = "!text-accent-blue-soft-foreground";
export const FOREGROUND_CLASSNAMES: Partial<
  Record<AlertStatus, AlertStatusClassNames>
> = {
  critical: {
    indicator: CRITICAL_FOREGROUND,
    title: CRITICAL_FOREGROUND,
    content: CRITICAL_FOREGROUND,
  },
  info: {
    indicator: INFO_FOREGROUND,
    title: INFO_FOREGROUND,
    content: INFO_FOREGROUND,
  },
};

/** `--soft-muted`: secondary text on *-soft fills (translucent so the tint shows through). */
const SOFT_DESCRIPTION = { description: "!text-soft-muted" };

/** v3 *-soft backgrounds and `--soft-muted` descriptions for the soft alert variant. */
export const SOFT_COLOR_CLASSNAMES: Partial<
  Record<AlertStatus, AlertStatusClassNames>
> = {
  default: { base: "bg-default-soft shadow-none", ...SOFT_DESCRIPTION },
  primary: { base: "bg-accent-soft shadow-none", ...SOFT_DESCRIPTION },
  info: { base: "bg-accent-blue-soft shadow-none", ...SOFT_DESCRIPTION },
  success: { base: "bg-success-soft shadow-none", ...SOFT_DESCRIPTION },
  warning: { base: "bg-warning-soft shadow-none", ...SOFT_DESCRIPTION },
  danger: { base: "bg-danger-soft shadow-none", ...SOFT_DESCRIPTION },
  critical: { base: "bg-critical-soft shadow-none", ...SOFT_DESCRIPTION },
};
